import styles from "../../../../styles/races/national/MapWidget.module.scss";
import playButton from "../../../../../public/images/icons/play-button.svg";
import pauseButton from "../../../../../public/images/icons/pause-button.svg";
import React, { useEffect, useState, useReducer, useRef, useCallback } from "react";
import Image from "next/image";

const reducer = (_, payload) => {
  return payload;
}

// source: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
const useInterval = (callback, delay) => {
  const savedCallback = useRef();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
};

const loader = ({ src, width, quality }) => {
  const imageUrl = process.env.SITE_URL;
  return `${imageUrl}${src}?w=${width}&q=${quality || 75}`;
};

const MapTimeSlider = ({ onChange, maxTimestamp, minTimestamp, interval, markerInterval, lastTimestamp, isPlaying, onPlay, onPause }) => {
  const maxStep = useCallback(() => {
    if(!maxTimestamp || !minTimestamp || !interval) {
      return 1;
    }
    return (maxTimestamp - minTimestamp) / interval;
  }, [maxTimestamp, minTimestamp, interval]);

  const playbackSpeedMs = maxStep() > 10 ? 100 : 300;
  const [thumbPosition, setThumbPosition] = useReducer(reducer, 0);
  const [tooltip, setTooltip] = useState("");
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isPlayButton, setIsPlayButton] = useState(true);

  useEffect(() => {
    setThumbPosition(maxStep());
  }, [maxStep]);

  useInterval(() => {
    if(!isPlaying) {
      return;
    }

    if(thumbPosition === maxStep()) {
      onPause();
      callOnChange(thumbPosition);
    }
    else {
      setThumbPosition(thumbPosition + 1);
      callOnChange(thumbPosition);
    }
  }, playbackSpeedMs);

  const togglePlay = () => {
    if(isPlaying) {
      onPause();
    } else {
      onPlay();
    }

    if(thumbPosition === maxStep()) {
      setThumbPosition(0);
    }
  };

  const callOnChange = useCallback((_thumbPosition) => {
    const timeStep = Math.floor(
      minTimestamp + (_thumbPosition + 1) * 
      ((maxTimestamp - minTimestamp) / maxStep())
    );
    if(onChange) {
      onChange(timeStep);
    }
  }, [maxStep, maxTimestamp, minTimestamp, onChange]);

  const handleSliderChange = useCallback((event) => {
    event.stopPropagation();

    const inputEl = document.querySelector("input[type='range']");
    const _thumbPosition = parseInt(inputEl.value);
    
    setThumbPosition(_thumbPosition);
    callOnChange(_thumbPosition);
  }, [callOnChange]);

  useEffect(() => {
    const formatTime = (timestamp) => {
      return (new Date(timestamp)).toLocaleString("en-US", { 
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hourCycle: "h12" 
      })
    };
    // const timeStep = Math.floor(
    //   minTimestamp + (thumbPosition + 1) * 
    //   ((maxTimestamp - minTimestamp) / maxStep())
    // );
    if (lastTimestamp) {
      setTooltip(`As of ${formatTime(lastTimestamp)}`);
    } else {
      setTooltip("");
    }
  }, [lastTimestamp]);

  const [timeMarkers, setTimeMarkers] = useState([]);

  useEffect(() => {
    if(!minTimestamp || !maxTimestamp || !markerInterval) {
      return;
    }
    const markers = [];
    markers.push({ timestamp: minTimestamp, position: 0 });
    let current = minTimestamp + markerInterval;
    while(current <= maxTimestamp) {
      const roundedHour = (timestamp) => timestamp - (timestamp % (60 * 60 * 1000));
      markers.push({
        timestamp: current,
        position: (100 * (roundedHour(current) - minTimestamp - interval) / (maxTimestamp - minTimestamp))
      });
      current = current + markerInterval;
    };
    if (markers?.length) {
      if(markers[markers.length - 1]?.position < 90) {
        markers.push({ timestamp: maxTimestamp, position: 100 });
      }
      if(markers[1]?.position < 10) {
        markers.splice(0, 1);
      }
    }
    setTimeMarkers(markers);
  }, [markerInterval, setTimeMarkers, maxTimestamp, minTimestamp, interval]);

  const getFormattedTime = (timestamp, showMinutes = false) => {
    const timestampDate = showMinutes ? new Date(timestamp) : new Date(timestamp);
    const hours = timestampDate.getHours();
    const minutes = timestampDate.getMinutes();
    const suffix =
    hours === 12 ? "NN" :
    hours === 0 ? "MN" :
    hours < 12 ? "AM" :
    "PM";

    return `${hours % 12 === 0 ? 12 : hours % 12}${true ? `:${minutes < 10 ? `0${minutes}` : minutes}` : ""} ${suffix}`;
  };

  const getTooltipPosition = () => {
    const position = `min(calc(${((thumbPosition / maxStep()) * 100).toFixed(2)}% - \
      ${(34 * (thumbPosition / maxStep())) - 17}px), calc(100% - 90px)`;
    return position;
  }

  return (
    <div className={styles.sliderWrap}>
      <div 
        className={`${styles.button} ${!isPlayButton ? styles.pause : styles.play}`} 
        onClick={async () => { setIsPlayButton((s) => { return !s }); togglePlay(); }}
      >
        { !isPlayButton ?
          <svg width="158" height="189" viewBox="0 0 158 189" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="60" height="189" fill="white"/>
            <rect x="98" width="60" height="189" fill="white"/>
          </svg> :
          <svg width="173" height="200" viewBox="0 0 173 200" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M173 100L0.499991 199.593L0.499999 0.407074L173 100Z" fill="white"/>
          </svg> 
        }
      </div>
      <div className={styles.track}>
        { lastTimestamp && isTooltipOpen &&
          <div className={styles.sliderTooltip} style={{ left: getTooltipPosition() }}>
            { tooltip }
          </div>
        }
        <input
          onMouseOver={() => setIsTooltipOpen(true)}
          onMouseLeave={() => setIsTooltipOpen(false)}
          onTouchStart={() => setIsTooltipOpen(true)}
          onTouchEnd={() => setIsTooltipOpen(false)}
          onTouchCancel={() => setIsTooltipOpen(false)}
          type="range"
          min={0}
          max={maxStep()}
          value={thumbPosition}
          onInput={handleSliderChange}
          onChange={handleSliderChange}
          className={styles.slider}
        />
        <div className={styles.markers}>
          {
            timeMarkers.map((marker, index) => {
              return (
                <div key={index} className={styles.marker}
                style={{ transform: `translateX(calc(${marker.position.toFixed(4)}% - 34px))` }}
                >
                  <div className={styles.markerInner}>
                    <div>{ (new Date(marker.timestamp)).toLocaleDateString("en-US").replace("2022", "22") }</div>
                    <div>{ getFormattedTime(marker.timestamp, marker.position === 100 || marker.position === 0) }</div>
                  </div>
                </div>
              )
            })
          }
        </div>
      </div>
    </div>
  )
};

export default MapTimeSlider;